import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, unref as _unref, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "edit-title-container"
}
const _hoisted_2 = { class: "field" }
const _hoisted_3 = {
  key: 1,
  class: "edit-utterances-container"
}

import Modal from '@/shared/components/Modal.vue';
import { maxLength, required } from '@vuelidate/validators';
import VuelidateUtil from '@/shared/util/vuelidateUtil';
import Toast from '@/shared/services/toast';
import { IntentViewModel } from '@/features/bots/models/intents/intentViewModel';
import { UtteranceViewModel } from '@/features/bots/models/intents/utteranceViewModel';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import UtteranceList from '@/features/bots/components/designer/UtteranceList.vue';
import { NLP_ENABLED } from '@/store/app/featureTypes';
import { GET_INTENT, UPDATE_INTENT } from '@/store/intents/actionTypes';
import { SaveIntentViewModel } from '@/features/bots/models/intents/saveIntentViewModel';
import { useStore } from 'vuex';
import { computed, onMounted, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';


export default /*@__PURE__*/_defineComponent({
  __name: 'EditIntentModal',
  props: {
  intentId: {
    type: String,
    required: true,
  },
},
  emits: ["failedToLoad", "closed", "completed"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const store = useStore();
const isWorkspaceFeatureEnabled = (feature: string, defaultValue = false) =>
  computed(() => store.getters.isWorkspaceFeatureEnabled(feature, defaultValue));
const getIntent = (id: string): Promise<IntentViewModel> => store.dispatch(GET_INTENT, id);
const updateIntent = (id: string, intent: SaveIntentViewModel): Promise<IntentViewModel> =>
  store.dispatch(UPDATE_INTENT, { id, intent });

const isSaveInProgress = ref(false);
const selectedIntent = ref<IntentViewModel>({} as IntentViewModel);
const selectedIntentId = ref('');
const selectedIntentTitle = ref('');
const selectedIntentUtterances = ref<UtteranceViewModel[]>([]);
const loading = ref(false);

const validations = {
  selectedIntentTitle: {
    required,
    maxLength: maxLength(50),
    intentNameValid: VuelidateUtil.intentName,
  },
  selectedIntentUtterances: {
    required,
    $each: helpers.forEach({
      text: {
        required,
        isUnique(value: string) {
          return !(
            selectedIntentUtterances.value &&
            selectedIntentUtterances.value.filter((utterance: UtteranceViewModel) => utterance.text === value).length >
              1
          );
        },
      },
    }),
  },
};

const $v = useVuelidate(validations, { selectedIntentTitle, selectedIntentUtterances });

onMounted(async () => {
  loading.value = true;
  await loadIntentWithId();
  setIntentToEdit();
  loading.value = false;
});

async function saveIntent() {
  $v.value.$touch();
  if ($v.value.$invalid) {
    Toast.showError('Fix the errors in red before saving');
    return;
  }
  try {
    if (nlpEnabled()) {
      isSaveInProgress.value = true;
      selectedIntent.value.utterances = selectedIntentUtterances.value.sort(
        (uttOne: UtteranceViewModel, uttTwo: UtteranceViewModel) => uttOne.text.localeCompare(uttTwo.text),
      );
      selectedIntent.value.title = selectedIntentTitle.value.trim();

      const intent = {
        utterances: selectedIntent.value.utterances.map((utterance: UtteranceViewModel) => utterance.text),
        title: selectedIntent.value.title,
      };
      await updateIntent(selectedIntentId.value, intent);

      Toast.showSuccess('Saved intent');
      emit('completed', selectedIntent.value);

      isSaveInProgress.value = false;
      closeModal();
    } else {
      Toast.showError('Unable to save intent - your workspace must have Natural Language support enabled');
      closeModal();
    }
  } catch (ex: any) {
    Toast.showError(ex.message);
    isSaveInProgress.value = false;
  }
}

const errorMessage = computed(() => {
  const intentTitle = $v.value.selectedIntentTitle;
  if (intentTitle.required.$invalid) {
    return 'Name must not be empty';
  } else if (intentTitle.intentNameValid.$invalid) {
    return 'Name must not contain <, >, *, %, &, :, \\, $';
  } else if (intentTitle.maxLength.$invalid) {
    return 'Name must not be longer than 50 characters';
  }

  return '';
});

function isUnknownIntent(): boolean {
  return selectedIntentId.value === 'None';
}

async function loadIntentWithId() {
  try {
    selectedIntent.value = await getIntent(props.intentId);
  } catch (e) {
    Toast.showError('Error loading intent');
    emit('failedToLoad');
  }
}

function setIntentToEdit() {
  if (selectedIntent.value) {
    selectedIntentId.value = selectedIntent.value.id!;
    selectedIntentUtterances.value = selectedIntent.value.utterances.sort(
      (uttOne: UtteranceViewModel, uttTwo: UtteranceViewModel) => uttOne.text.localeCompare(uttTwo.text),
    );
    selectedIntentTitle.value = selectedIntent.value.title;
  }
}

function nlpEnabled() {
  return isWorkspaceFeatureEnabled(NLP_ENABLED).value;
}

function closeModal() {
  selectedIntentId.value = '';
  selectedIntentTitle.value = '';
  selectedIntentUtterances.value = [];
  emit('closed');
}

return (_ctx: any,_cache: any) => {
  const _component_n_tooltip = _resolveComponent("n-tooltip")!
  const _component_n_text_input = _resolveComponent("n-text-input")!
  const _component_n_loading_spinner = _resolveComponent("n-loading-spinner")!
  const _component_n_button = _resolveComponent("n-button")!
  const _component_n_button_group = _resolveComponent("n-button-group")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(Modal, { onCancel: closeModal }, {
      title: _withCtx(() => [...(_cache[2] || (_cache[2] = [
        _createElementVNode("p", { class: "modal-card-title" }, "Edit intent", -1)
      ]))]),
      body: _withCtx(() => [
        (!loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createElementVNode("h1", null, [
                _cache[3] || (_cache[3] = _createTextVNode(" Name ", -1)),
                (isUnknownIntent())
                  ? (_openBlock(), _createBlock(_component_n_tooltip, {
                      key: 0,
                      position: "right",
                      text: "The 'Unknown' intent is a system-defined intent and cannot be renamed"
                    }))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_2, [
                _createVNode(_component_n_text_input, {
                  class: "edit-title",
                  modelValue: selectedIntentTitle.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedIntentTitle).value = $event)),
                  hideLabel: true,
                  label: "Intent name",
                  disabled: isUnknownIntent() || isSaveInProgress.value,
                  errorMessage: errorMessage.value
                }, null, 8, ["modelValue", "disabled", "errorMessage"])
              ])
            ]))
          : _createCommentVNode("", true),
        (!loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _cache[4] || (_cache[4] = _createElementVNode("h1", null, "Example Utterances", -1)),
              _createVNode(UtteranceList, {
                modelValue: selectedIntentUtterances.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedIntentUtterances).value = $event)),
                v: _unref($v).selectedIntentUtterances,
                term: "utterance",
                "is-editable": !isSaveInProgress.value
              }, null, 8, ["modelValue", "v", "is-editable"])
            ]))
          : _createCommentVNode("", true),
        (loading.value)
          ? (_openBlock(), _createBlock(_component_n_loading_spinner, {
              key: 2,
              class: "loading-spinner"
            }))
          : _createCommentVNode("", true)
      ]),
      buttons: _withCtx(() => [
        _createVNode(_component_n_button_group, null, {
          default: _withCtx(() => [
            _createVNode(_component_n_button, {
              variant: "primary",
              onClick: saveIntent,
              loading: isSaveInProgress.value
            }, {
              default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                _createTextVNode("Save ", -1)
              ]))]),
              _: 1
            }, 8, ["loading"]),
            _createVNode(_component_n_button, {
              onClick: closeModal,
              variant: "primary--outline",
              disabled: isSaveInProgress.value
            }, {
              default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                _createTextVNode("Cancel", -1)
              ]))]),
              _: 1
            }, 8, ["disabled"])
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})